import { GripVertical, Plus, X } from 'lucide-react'
import { useState } from 'react'

import { Combobox } from '@/components/Combobox'
import { Switch } from '@/components/ui/switch'

import { Hint, SectionLabel, selectClass } from '../_shared'
import {
  DEFAULT_DISPLAY,
  newField,
  type Aggregate,
  type DisplayKind,
  type DisplaySpec,
  type FieldSource,
  type FieldSpec,
  type IssueFieldsBlock,
} from '../config-types'

type Opt<T extends string> = { value: T; label: string }

const SOURCE_OPTS: Opt<FieldSource>[] = [
  { value: 'self', label: 'self' },
  { value: 'parent', label: 'parent' },
  { value: 'child', label: 'child' },
]
const AGGREGATE_OPTS: Opt<Aggregate>[] = [
  { value: 'sum', label: 'sum' },
  { value: 'count', label: 'count' },
  { value: 'list', label: 'list' },
  { value: 'min', label: 'min' },
  { value: 'max', label: 'max' },
]
const KIND_OPTS: Opt<DisplayKind>[] = [
  { value: 'auto', label: 'auto' },
  { value: 'status', label: 'status' },
  { value: 'priority', label: 'priority' },
  { value: 'user', label: 'user' },
  { value: 'date', label: 'date' },
  { value: 'labels', label: 'labels' },
  { value: 'number', label: 'number' },
  { value: 'text', label: 'text' },
]
const DATE_OPTS: Opt<DisplaySpec['date_format']>[] = [
  { value: 'relative', label: 'relative' },
  { value: 'dmy', label: 'dd.mm.yyyy' },
  { value: 'with_time', label: 'with time' },
  { value: 'iso', label: 'iso' },
]
const USER_OPTS: Opt<DisplaySpec['user_mode']>[] = [
  { value: 'avatar_name', label: 'avatar + name' },
  { value: 'name', label: 'name only' },
  { value: 'email', label: 'email' },
  { value: 'mention', label: '@mention' },
]
const STATUS_OPTS: Opt<DisplaySpec['status_style']>[] = [
  { value: 'lozenge', label: 'lozenge' },
  { value: 'text', label: 'text' },
]
const PRIORITY_OPTS: Opt<DisplaySpec['priority_mode']>[] = [
  { value: 'icon_name', label: 'icon + name' },
  { value: 'icon', label: 'icon only' },
  { value: 'name', label: 'name only' },
]
const LABELS_OPTS: Opt<DisplaySpec['labels_style']>[] = [
  { value: 'chips', label: 'chips' },
  { value: 'list', label: 'comma list' },
]

/** Native select over a fixed option list. */
function OptSelect<T extends string>({ value, options, onChange, label, className = '' }: {
  value: T; options: Opt<T>[]; onChange: (v: T) => void; label: string; className?: string
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      className={`${selectClass} w-full ${className}`}
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  )
}

/** Source change keeps the payload valid: aggregate exists only with source = child. */
function withSource(field: FieldSpec, source: FieldSource): FieldSpec {
  if (source === 'child') return { ...field, source, aggregate: field.aggregate ?? 'sum' }
  const { aggregate: _drop, ...rest } = field
  void _drop
  return { ...rest, source }
}

/** Format sub-options of the selected row (date format, user mode…), by display kind. */
function DisplayOptions({ display, onChange }: { display: DisplaySpec; onChange: (d: DisplaySpec) => void }) {
  const row = (label: string, control: React.ReactNode) => (
    <div className="contents">
      <span className="self-center text-text-3">{label}</span>
      <div className="max-w-[240px]">{control}</div>
    </div>
  )
  switch (display.kind) {
    case 'date':
      return (
        <>
          {row('Date format', <OptSelect label="Date format" value={display.date_format} options={DATE_OPTS}
            onChange={(date_format) => onChange({ ...display, date_format })} />)}
          {row('Highlight overdue', <Switch checked={display.overdue} aria-label="Highlight overdue"
            onCheckedChange={(overdue) => onChange({ ...display, overdue })} />)}
        </>
      )
    case 'user':
      return row('User display', <OptSelect label="User display" value={display.user_mode} options={USER_OPTS}
        onChange={(user_mode) => onChange({ ...display, user_mode })} />)
    case 'status':
      return row('Status display', <OptSelect label="Status display" value={display.status_style} options={STATUS_OPTS}
        onChange={(status_style) => onChange({ ...display, status_style })} />)
    case 'priority':
      return row('Priority display', <OptSelect label="Priority display" value={display.priority_mode} options={PRIORITY_OPTS}
        onChange={(priority_mode) => onChange({ ...display, priority_mode })} />)
    case 'labels':
      return row('Labels display', <OptSelect label="Labels display" value={display.labels_style} options={LABELS_OPTS}
        onChange={(labels_style) => onChange({ ...display, labels_style })} />)
    default:
      return null
  }
}

/**
 * Fields of an issue_fields block as a table — Field / Source / Aggregate / Format,
 * rows drag to reorder. Options of the selected row (label, format details) sit below.
 */
export function FieldRowList({
  block,
  onChange,
}: {
  block: IssueFieldsBlock
  onChange: (b: IssueFieldsBlock) => void
}) {
  const [selected, setSelected] = useState(0)
  const [dragFrom, setDragFrom] = useState<number | null>(null)
  const [dragOver, setDragOver] = useState<number | null>(null)
  const fields = block.fields
  const sel = fields.length ? Math.min(selected, fields.length - 1) : -1
  const current = sel >= 0 ? fields[sel] : null

  const setField = (i: number, f: FieldSpec) => onChange({ ...block, fields: fields.map((x, idx) => (idx === i ? f : x)) })
  const move = (from: number, to: number) => {
    if (from === to) return
    const next = [...fields]
    const [it] = next.splice(from, 1)
    next.splice(to, 0, it)
    onChange({ ...block, fields: next })
    setSelected(to)
  }
  const remove = (i: number) => {
    onChange({ ...block, fields: fields.filter((_, idx) => idx !== i) })
    setSelected((s) => (i < s ? s - 1 : s))
  }

  const th = 'border-b border-line px-1.5 py-1.5 text-left text-[11px] font-medium uppercase tracking-[0.05em] text-text-3'
  return (
    <div>
      <table aria-label="Fields" className="w-full table-fixed border-separate border-spacing-0 text-[12.5px]">
        <colgroup>
          <col style={{ width: 22 }} />
          <col />
          <col style={{ width: '16%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '18%' }} />
          <col style={{ width: 30 }} />
        </colgroup>
        <thead>
          <tr>
            <th className={th}><span className="sr-only">Reorder</span></th>
            <th className={th}>Field</th>
            <th className={th}>Source</th>
            <th className={th}>Aggregate</th>
            <th className={th}>Format</th>
            <th className={th}><span className="sr-only">Remove</span></th>
          </tr>
        </thead>
        <tbody>
          {fields.map((f, i) => {
            const display = f.display ?? DEFAULT_DISPLAY
            const on = i === sel
            const name = f.label || f.field_id || `field ${i + 1}`
            return (
              <tr
                key={i}
                draggable
                data-selected={on || undefined}
                onClick={() => setSelected(i)}
                onFocus={() => setSelected(i)}
                onDragStart={(e) => { setDragFrom(i); e.dataTransfer.effectAllowed = 'move' }}
                onDragOver={(e) => { if (dragFrom !== null) { e.preventDefault(); setDragOver(i) } }}
                onDrop={(e) => {
                  e.preventDefault()
                  if (dragFrom !== null) move(dragFrom, i)
                  setDragFrom(null)
                  setDragOver(null)
                }}
                onDragEnd={() => { setDragFrom(null); setDragOver(null) }}
                className={[on ? 'bg-surface' : '', dragOver === i && dragFrom !== i ? 'outline outline-1 -outline-offset-1 outline-primary' : ''].join(' ')}
              >
                <td className="border-b border-line-row px-1 py-1 align-middle">
                  <GripVertical aria-hidden className="size-3.5 cursor-grab text-text-3" />
                </td>
                <td className="border-b border-line-row px-1.5 py-1">
                  <Combobox
                    source="field"
                    deps={{}}
                    multi={false}
                    value={f.field_id}
                    onChange={(field_id) => setField(i, { ...f, field_id })}
                    ariaLabel={`Field ${i + 1}`}
                    className=""
                  />
                </td>
                <td className="border-b border-line-row px-1.5 py-1">
                  <OptSelect label={`Source of ${name}`} value={f.source} options={SOURCE_OPTS}
                    onChange={(source) => setField(i, withSource(f, source))} />
                </td>
                <td className="border-b border-line-row px-1.5 py-1">
                  {f.source === 'child'
                    ? <OptSelect label={`Aggregate of ${name}`} value={f.aggregate ?? 'sum'} options={AGGREGATE_OPTS}
                        onChange={(aggregate) => setField(i, { ...f, source: 'child', aggregate })} />
                    : <span className="px-2 text-text-3" title="Aggregate applies to child fields only">—</span>}
                </td>
                <td className="border-b border-line-row px-1.5 py-1">
                  <OptSelect label={`Format of ${name}`} value={display.kind} options={KIND_OPTS}
                    onChange={(kind) => setField(i, { ...f, display: { ...display, kind } })} />
                </td>
                <td className="border-b border-line-row px-1 py-1 text-center">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); remove(i) }}
                    aria-label={`Remove ${name}`}
                    className="inline-flex size-6 items-center justify-center rounded text-text-3 transition-colors hover:bg-surface-raised hover:text-danger"
                  >
                    <X aria-hidden className="size-3.5" />
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <button
        type="button"
        onClick={() => { onChange({ ...block, fields: [...fields, newField()] }); setSelected(fields.length) }}
        className="mt-2.5 inline-flex items-center gap-1 rounded-md border border-line-strong bg-surface-raised px-2.5 py-1 text-[12px] text-foreground transition-colors hover:bg-surface-active"
      >
        <Plus aria-hidden className="size-3.5" />
        Add field
      </button>

      {current && (
        <div className="mt-5">
          <SectionLabel>Row options · {current.label || current.field_id || `field ${sel + 1}`}</SectionLabel>
          <div className="grid grid-cols-[140px_minmax(0,1fr)] gap-x-3 gap-y-2 text-[12.5px]">
            <label htmlFor="field-row-label" className="self-center text-text-3">Label</label>
            <input
              id="field-row-label"
              value={current.label ?? ''}
              onChange={(e) => setField(sel, { ...current, label: e.target.value })}
              placeholder="defaults to the field name"
              className="h-7 max-w-[240px] rounded-md border border-input bg-surface px-2 text-[12.5px] text-foreground placeholder:text-text-3"
            />
            {current.source === 'child'
              ? (
                <>
                  <span className="text-text-3">Child values</span>
                  <span className="text-text-2">many children → one value: the aggregate ({current.aggregate ?? 'sum'})</span>
                </>
              )
              : <DisplayOptions display={current.display ?? DEFAULT_DISPLAY}
                  onChange={(d) => setField(sel, { ...current, display: d })} />}
          </div>
        </div>
      )}
      <Hint>Fields group by source → up to 3 queries (self / parent / child), not one per field.</Hint>
    </div>
  )
}
